import { Fragment } from "react";
import { Dialog, DialogBody, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Kbd } from "@/components/ui/input";
import { useDesktopPrefs } from "@/features/desktop/useDesktopPrefs";
import { ui, useUi } from "@/features/ui-state";
import { acceleratorKeys, SHORTCUTS, type Shortcut } from "./shortcuts";

function Keys({ keys, then }: Pick<Shortcut, "keys" | "then">) {
  return (
    <span className="flex shrink-0 flex-wrap items-center justify-end gap-1">
      {keys.map((combo, i) => (
        <Fragment key={i}>
          {i > 0 && <span className="px-0.5 text-[11.5px] text-subtle">{then ? "then" : "or"}</span>}
          <span className="inline-flex gap-0.5">
            {combo.map((k, j) => (
              <Kbd key={j}>{k}</Kbd>
            ))}
          </span>
        </Fragment>
      ))}
    </span>
  );
}

function Item({ label, keys, then }: Pick<Shortcut, "label" | "keys" | "then">) {
  return (
    <li className="flex min-h-7 items-center gap-3 text-[13.5px]">
      <span className="min-w-0 flex-1">{label}</span>
      <Keys keys={keys} then={then} />
    </li>
  );
}

export function ShortcutsDialog() {
  const open = useUi((s) => s.shortcutsOpen);
  const [desktop] = useDesktopPrefs();
  const globalKeys = desktop?.quickAddShortcut ? acceleratorKeys(desktop.quickAddShortcut) : null;
  return (
    <Dialog open={open} onOpenChange={(o) => ui.set({ shortcutsOpen: o })}>
      <DialogContent className="max-w-[720px]">
        <DialogBody className="max-h-[78vh] overflow-y-auto">
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <div className="grid gap-x-8 gap-y-5 pt-1 sm:grid-cols-2">
            {[SHORTCUTS.slice(0, -1), SHORTCUTS.slice(-1)].map((column, i) => (
              <div key={i} className="flex flex-col gap-5">
                {column.map((g) => (
                  <section key={g.title}>
                    <h3 className="mb-1.5 text-[12.5px] font-semibold text-muted-foreground">{g.title}</h3>
                    <ul className="flex flex-col">
                      {g.items.map((s) => (
                        <Item key={s.id} {...s} />
                      ))}
                      {g.title === "General" && globalKeys && <Item label="New task from any app" keys={[globalKeys]} />}
                    </ul>
                  </section>
                ))}
              </div>
            ))}
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
